import type { QuestionDraft } from './survey-questions-form'
import type { SurveyQuestion } from '~/gql/surveys/surveys'
import { useMutation } from '@apollo/client/react'
import { Loader, Plus } from 'lucide-react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { GQL_REPLACE_SURVEY_QUESTIONS } from '~/gql/surveys/surveys'
import { tourAnchor } from '~/helpers/tour/anchors'
import { addQuestion, draftsFrom, moveQuestion, questionsError, setOverall, SURVEY_LIMITS } from './survey-questions-form'
import SurveyQuestionRow from './SurveyQuestionRow'

/**
 * L'édition du questionnaire (#320), sur les questions déjà chargées : elles
 * forment l'état initial, sans effet qui les recopierait après coup.
 */
function SurveyQuestionsEditor({ initial }: { initial: readonly SurveyQuestion[] }) {
  const { t } = useTranslation()
  const overallName = useId()
  const [save, { loading: saving }] = useMutation(GQL_REPLACE_SURVEY_QUESTIONS)
  const [drafts, setDrafts] = useState<QuestionDraft[]>(() => draftsFrom(initial))

  const submit = async () => {
    const error = questionsError(drafts)
    if (error) return toast.error(t(error))
    try {
      await save({ variables: { questions: drafts.map(({ kind, label, isOverall }) => ({ kind, label, isOverall })) } })
      toast.success(t('surveyEditor.saved'))
    } catch {
      toast.error(t('surveyEditor.saveFailed'))
    }
  }

  return (
    <div className="flex w-full max-w-3xl flex-col gap-6">
      <p className="text-sm text-muted-foreground" {...tourAnchor('surveyEditor.intro')}>{t('surveyEditor.intro')}</p>
      <ol className="flex flex-col gap-3" {...tourAnchor('surveyEditor.questions')}>
        {drafts.map((draft, index) => (
          <SurveyQuestionRow
            key={draft.key}
            draft={draft}
            index={index}
            count={drafts.length}
            overallName={overallName}
            onChange={next => setDrafts(ds => ds.map(d => (d.key === draft.key ? next : d)))}
            onOverall={() => setDrafts(ds => setOverall(ds, draft.key))}
            onMove={by => setDrafts(ds => moveQuestion(ds, index, by))}
            onRemove={() => setDrafts(ds => ds.filter(d => d.key !== draft.key))}
          />
        ))}
      </ol>
      <div className="flex flex-wrap justify-between gap-2">
        <Button variant="outline" disabled={drafts.length >= SURVEY_LIMITS.questions} onClick={() => setDrafts(addQuestion)} {...tourAnchor('surveyEditor.add')}>
          <Plus className="mr-2 size-4" />
          {t('surveyEditor.add')}
        </Button>
        <Button onClick={() => void submit()} disabled={saving} {...tourAnchor('surveyEditor.save')}>
          {saving && <Loader className="mr-2 size-4 animate-spin" />}
          {t('common.save')}
        </Button>
      </div>
    </div>
  )
}

export default SurveyQuestionsEditor
